export const GOOGLE_AUTH_MESSAGE_TYPE = 'twin-tales-google-auth';

const POPUP_FEATURES = 'width=520,height=720,scrollbars=yes,resizable=yes';

function isDesktopOAuth() {
  return typeof window !== 'undefined' && Boolean(window.twinTales?.onOAuthPopupResult);
}

function oauthPopupWindowName() {
  return `twin-tales-google-auth-${Date.now()}`;
}

function isPopupClosed(popup) {
  if (!popup) return true;
  try {
    return popup.closed;
  } catch {
    return true;
  }
}

export function openGoogleAuthWindow(authorizationUrl) {
  let win = null;
  try {
    win = window.open(authorizationUrl, oauthPopupWindowName(), POPUP_FEATURES);
  } catch {
    win = null;
  }
  if (win) {
    try {
      win.focus();
    } catch {
      /* cross-origin */
    }
    return win;
  }
  try {
    win = window.open(authorizationUrl, '_blank');
    if (win) win.focus();
  } catch {
    win = null;
  }
  return win;
}

export function resolveGoogleAuthFrontendOrigin() {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  if (origin && origin !== 'null') return origin;
  if (typeof window !== 'undefined' && window.twinTales?.isDesktop) return 'file://';
  return origin;
}

/**
 * @param {{ popupRef: import('react').MutableRefObject<Window | null>, onResult: (payload: { status: string, message?: string, token?: string, user?: object, exchange_code?: string }) => void, onPopupClosed?: () => void }} opts
 */
export function subscribeGoogleAuthResult({ popupRef, onResult, onPopupClosed }) {
  let settled = false;
  const deliver = (data) => {
    if (settled) return;
    if (!data || data.type !== GOOGLE_AUTH_MESSAGE_TYPE) return;
    settled = true;
    onResult({
      status: String(data.status || ''),
      message: String(data.message || ''),
      token: data.token ? String(data.token) : undefined,
      user: data.user && typeof data.user === 'object' ? data.user : undefined,
      exchange_code: data.exchange_code ? String(data.exchange_code) : undefined,
      is_new_user: Boolean(data.is_new_user),
    });
  };

  const onMessage = (event) => {
    const data = event.data;
    if (!data || data.type !== GOOGLE_AUTH_MESSAGE_TYPE) return;
    const popup = popupRef.current;
    if (popup && event.source && event.source !== popup) return;
    deliver(data);
  };

  window.addEventListener('message', onMessage);

  let cleanupDesktop = () => {};
  if (isDesktopOAuth()) {
    cleanupDesktop = window.twinTales.onOAuthPopupResult((payload) => {
      if (payload?.type && payload.type !== GOOGLE_AUTH_MESSAGE_TYPE) return;
      deliver({
        type: GOOGLE_AUTH_MESSAGE_TYPE,
        status: payload?.status,
        message: payload?.message,
        token: payload?.token,
        user: payload?.user,
        exchange_code: payload?.exchange_code,
        is_new_user: payload?.is_new_user,
      });
    });
    if (window.twinTales.onOAuthPopupClosed) {
      const cleanupClosed = window.twinTales.onOAuthPopupClosed(() => {
        if (settled) return;
        onPopupClosed?.();
      });
      const prevCleanup = cleanupDesktop;
      cleanupDesktop = () => {
        prevCleanup();
        cleanupClosed();
      };
    }
  }

  let closedNotified = false;
  let intervalId = null;
  if (!isDesktopOAuth()) {
    intervalId = window.setInterval(() => {
      const popup = popupRef.current;
      if (!popup || closedNotified) return;
      if (!isPopupClosed(popup)) return;
      closedNotified = true;
      if (!settled) onPopupClosed?.();
    }, 400);
  }

  return () => {
    window.removeEventListener('message', onMessage);
    if (intervalId != null) window.clearInterval(intervalId);
    cleanupDesktop();
  };
}

export function persistGoogleAuthSession({ token, user }) {
  if (token) localStorage.setItem('storycraft_token', token);
  if (user) localStorage.setItem('storycraft_user', JSON.stringify(user));
  import('@/lib/journeyTracker').then(({ identifyJourneySession }) => identifyJourneySession());
}
